// @flow strict

import React, { useState, Fragment } from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import { useLanguageContext } from '~contexts/language-context';
import { LanguageSwitchIcon } from '~components/icons/language-switch-icon';

import type { Alternates } from '~types/post';

import * as styles from './language-switch.module.less';

/**
 * Type of component properties.
 */
type LanguageSwitchProps = {
  alternates: Alternates,
};

/**
 * Type of list item.
 */
type LanguageSwitchItemProps = {
  language: string,
  hideIcon?: boolean,
};

/**
 * Component of a single item in language switch.
 *
 * @param {string} language - language code
 * @param {boolean=false} hideIcon - flag to hide icon
 *
 * @returns {Node}
 */
const LanguageSwitchItem = ({
  language,
  hideIcon = false,
}: LanguageSwitchItemProps) => {
  return (
    <div className={styles.languageSwitchItem}>
      <span className={styles.value}>{language}</span>
      {!hideIcon && <LanguageSwitchIcon />}
    </div>
  );
};

/**
 * Component to change the language of the blog.
 *
 * @param alternates - urls of current page for different languages
 *
 * @returns {Node}
 */
export const LanguageSwitch = ({ alternates }: LanguageSwitchProps) => {
  // get domains for each language
  const data = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          domain
        }
      }
    }
  `);

  // turn array into object with language code index
  const domain = data.site.siteMetadata.domain;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { language, languages } = useLanguageContext();

  // click handler to open select drop down
  const openSelect = () => {
    setIsOpen(true);
  };

  // click handler to close select drop down
  const closeSelect = () => {
    setIsOpen(false);
  };

  // all languages but the current one is selectable
  const selectableLanguages = languages.filter((lang) => lang !== language);

  return (
    <div className={styles.languageSwitch}>
      <div className={styles.languageSwitchWrapper} onClick={openSelect}>
        <LanguageSwitchItem language={language} />
      </div>

      {/* render drop down if state is open */}
      {isOpen && (
        <Fragment>
          <div className={styles.modal} onClick={closeSelect} />
          <div className={styles.popover}>
            <LanguageSwitchItem language={language} hideIcon={true} />

            {selectableLanguages.map((lang) => {
              let href = alternates[lang];
              // if site does not have an alternate url
              // check if there is a fallback being passed
              if (href == null) {
                href = alternates.fallback[lang] || '/';
              }

              return (
                <a
                  href={href}
                  key={lang}
                  className={language === lang ? 'active' : ''}
                >
                  <LanguageSwitchItem language={lang} hideIcon={true} />
                </a>
              );
            })}
          </div>
        </Fragment>
      )}
    </div>
  );
};
